import {DataTable} from "../DataTable/DataTable";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {ServiceEventTicketPolicyEntity} from "@ots-frontend/ots-data";
import {useServiceEventTicketPolicyDataTableFilter} from "./useServiceEventTicketPolicyDataTableFilter";
import {useNavigate} from "react-router-dom";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {
  useServiceEventTicketPolicyAction
} from "../../menu/ServiceEventTicketPolicyActionMenu/useServiceEventTicketPolicyAction";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: ServiceEventTicketPolicyEntity[] = []

export type ServiceEventTicketPolicyDataTableProps = EntityDataTableProps<ServiceEventTicketPolicyEntity>

export const ServiceEventTicketPolicyDataTable = (props: ServiceEventTicketPolicyDataTableProps) => {
  const {
    records: roles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useServiceEventTicketPolicyDataTableFilter({
    records: roles
  })

  const sortConfig = useMemo(() => {
    return {
      "eventType": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.eventType?.code?.toLowerCase()
          }])
        }
      },
      "company": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.companyName?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onServiceEventTicketPolicyActionClick
  } = useServiceEventTicketPolicyAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventTicketPolicyEntity) => {
    e.stopPropagation()
    navigate(`/service-event-ticket-policies/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventTicketPolicyEntity) => {
    e.stopPropagation()
    onServiceEventTicketPolicyActionClick(record, 'EDIT')
  }, [onServiceEventTicketPolicyActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventTicketPolicyEntity) => {
    e.stopPropagation()
    onServiceEventTicketPolicyActionClick(record, 'DELETE')
  }, [onServiceEventTicketPolicyActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceEventTicketPolicyEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.serviceeventticketpolicy.table.column.name", "Name"),
        accessor: 'name',
        width: 200,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record}/>
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "eventType"),
        title: t("common.entity.serviceeventticketpolicy.table.column.eventtype", "Event Type"),
        accessor: 'eventType',
        width: 200,
        sortable: true,
        render: ({eventType}) => (
          <FormattedDatabaseCode value={eventType}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "company"),
        title: t("common.entity.serviceeventticketpolicy.table.column.company", "Company"),
        accessor: 'companyId',
        width: 200,
        sortable: true,
        render: ({companyId, companyName}) => (
          <EntityAnchor value={{objectId: companyId, objectType: "COMPANY", objectName: companyName}}/>
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "serviceType"),
        title: t("common.entity.serviceeventticketpolicy.table.column.servicetype", "Service Type"),
        accessor: 'serviceTypeName',
        width: 200,
        sortable: true,
        render: ({serviceTypeName}) => (
          <DefaultText value={serviceTypeName}/>
        )
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (record) => (
          <EntityActionGroup
            permissionPrefix={"serviceeventticketpolicy"}
            onViewActionClick={(e) => onViewActionClick(e, record)}
            onEditActionClick={(e) => onEditActionClick(e, record)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, record)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onViewActionClick, onEditActionClick, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
